"use client";

// ============================================================================
// Globaler Promise-basierter Bestätigungs-Dialog — Ersatz für window.confirm()
// ----------------------------------------------------------------------------
// BFSG / WCAG: window.confirm() ist nicht barrierefrei (nicht als ARIA-Dialog
// angekündigt, kein DOM-Fokus-Trap, kein Theming, von Screenreadern uneinheitlich
// behandelt). Dieser Provider stellt eine `confirm()`-Funktion bereit, die einen
// Radix-AlertDialog (role="alertdialog", Fokus-Trap, Escape, Fokus-Rückgabe)
// öffnet und ein Promise<boolean> auflöst:
//
//   const confirm = useConfirm();
//   if (await confirm({ title: "Wirklich löschen?", destructive: true })) { … }
//
// Ein einziger Dialog wird global (im Provider) gerendert, statt pro Aufrufstelle
// AlertDialog-JSX zu duplizieren. Für deklarative Einzelfälle existiert weiterhin
// die <ConfirmDialog>-Komponente (confirm-dialog.tsx).
// ============================================================================

import * as React from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

export interface ConfirmOptions {
  title?: string;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** Rot eingefärbter Bestätigen-Button für destruktive Aktionen (löschen etc.). */
  destructive?: boolean;
}

type ConfirmFn = (opts?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [opts, setOpts] = React.useState<ConfirmOptions>({});
  const resolverRef = React.useRef<((value: boolean) => void) | null>(null);

  const settle = React.useCallback((result: boolean) => {
    setOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(result);
  }, []);

  const confirm = React.useCallback<ConfirmFn>((o) => {
    return new Promise<boolean>((resolve) => {
      // Falls ein vorheriger Dialog noch offen war: alten Resolver mit false
      // beenden, bevor wir überschreiben.
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setOpts(o ?? {});
      setOpen(true);
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          // Escape / Schließen ohne Button-Klick = Abbrechen.
          if (!next) settle(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{opts.title ?? "Bist du sicher?"}</AlertDialogTitle>
            {opts.description != null && (
              <AlertDialogDescription>{opts.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>
              {opts.cancelText ?? "Abbrechen"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              className={cn(
                opts.destructive &&
                  "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/40",
              )}
            >
              {opts.confirmText ?? "Bestätigen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

/**
 * Liefert eine `confirm(opts)`-Funktion, die ein Promise<boolean> auflöst.
 * Muss innerhalb von <ConfirmProvider> verwendet werden (global gemountet).
 */
export function useConfirm(): ConfirmFn {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm muss innerhalb von <ConfirmProvider> verwendet werden.");
  }
  return ctx;
}
